import React from 'react';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';

import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';

import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined';

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';

import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined';

import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';

import PublishOutlinedIcon from '@mui/icons-material/PublishOutlined';

import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';

import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';

import SourceOutlinedIcon from '@mui/icons-material/SourceOutlined';

import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';

import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';

import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

import {
  BieRecordId,
  IBieOnboardingValidationIssue,
  IBieOnboardingWorkbench,
  IBieOnboardingWorkbenchImport,
  getBieImportClassLabel,
  getBieLoadSessionStateLabel,
  isBieFlagEnabled,
} from './bieonboardingworkbench.models';

import BieOnboardingImportCard from './BieOnboardingImportCard';

import BieOnboardingStatusChip from './BieOnboardingStatusChip';

import BieOnboardingSummaryCard from './BieOnboardingSummaryCard';

/* ============================================================
 * Types
 * ============================================================
 */

export interface IBieOnboardingOverviewProps {
  workbench: IBieOnboardingWorkbench | null;

  loading?: boolean;
  refreshing?: boolean;
  validating?: boolean;
  publishing?: boolean;

  errorMessage?: string;

  /**
   * Refreshes the complete Workbench contract.
   */
  onRefresh?: () => void;

  /**
   * Resolves and validates the staged workbook.
   */
  onValidate?: () => void;

  /**
   * Pack 6 publication action.
   *
   * This may remain undefined until Pack 6 is implemented.
   */
  onPublish?: () => void;

  /**
   * Opens the complete validation issue tab.
   */
  onViewAllIssues?: () => void;

  /**
   * Opens the Issues tab filtered to one Import.
   */
  onViewImportIssues?: (
    importId: BieRecordId,
    data: IBieOnboardingWorkbenchImport
  ) => void;

  /**
   * Opens the staged-record list for one Import.
   */
  onViewImportRecords?: (
    importId: BieRecordId,
    data: IBieOnboardingWorkbenchImport
  ) => void;

  /**
   * Opens the affected staged row for a recent issue.
   */
  onOpenIssueRecord?: (
    importItemId: BieRecordId,
    issue: IBieOnboardingValidationIssue
  ) => void;
}

/* ============================================================
 * Workflow presentation
 * ============================================================
 */

interface IBieWorkflowStep {
  code: string;
  label: string;
  description: string;

  completed: boolean;
  current: boolean;
  available: boolean;

  icon: React.ReactElement;
}

/* ============================================================
 * Component
 * ============================================================
 */

/**
 * Overview dashboard for the BIE Institutional Onboarding
 * Workbench.
 *
 * Presents:
 *
 * - Load Session identity and workflow state
 * - Source and planning-period information
 * - Validation summary KPIs
 * - Resolution and severity breakdowns
 * - Workflow readiness
 * - Import worksheet cards
 * - Recent validation issues
 */
export default class BieOnboardingOverview extends React.PureComponent<IBieOnboardingOverviewProps> {
  public render(): React.ReactNode {
    const {
      loading = false,
      refreshing = false,
      errorMessage,
      workbench,
    } = this.props;

    if (loading && !workbench) {
      return this.renderLoading();
    }

    if (!workbench) {
      return this.renderUnavailable();
    }

    return (
      <Stack spacing={2.5}>
        {refreshing && <LinearProgress />}

        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

        {this.renderLoadSessionHeader()}

        {this.renderSummaryCards()}

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 7 }}>
            {this.renderValidationBreakdown()}
          </Grid>

          <Grid size={{ xs: 12, lg: 5 }}>{this.renderWorkflowReadiness()}</Grid>
        </Grid>

        {this.renderImportSection()}

        {this.renderRecentIssues()}
      </Stack>
    );
  }

  /* ==========================================================
   * Load Session header
   * ==========================================================
   */

  private renderLoadSessionHeader(): React.ReactNode {
    const workbench = this.requireWorkbench();

    const { loadSession, summary } = workbench;

    return (
      <Paper
        variant="outlined"
        sx={{
          p: {
            xs: 2,
            md: 2.5,
          },

          position: 'relative',
          overflow: 'hidden',

          '&::before': {
            content: '""',

            position: 'absolute',
            top: 0,
            left: 0,

            width: 5,
            height: '100%',

            backgroundColor: (theme) => {
              switch (String(loadSession.stateCode).toUpperCase()) {
                case 'VALIDATED':
                  return theme.palette.success.main;

                case 'REJECTED':
                  return theme.palette.error.main;

                case 'STAGED':
                  return theme.palette.info.main;

                case 'PUBLISHED':
                  return theme.palette.primary.main;

                default:
                  return theme.palette.divider;
              }
            },
          },
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{
              xs: 'column',
              md: 'row',
            }}
            justifyContent="space-between"
            alignItems={{
              xs: 'stretch',
              md: 'flex-start',
            }}
            spacing={2}
          >
            <Box
              sx={{
                minWidth: 0,
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                useFlexGap
                flexWrap="wrap"
              >
                <Typography variant="h5" fontWeight={700}>
                  {loadSession.name}
                </Typography>

                <BieOnboardingStatusChip
                  kind="LOAD_SESSION"
                  value={loadSession.stateCode}
                />

                <BieOnboardingStatusChip
                  kind="VALIDATION"
                  value={summary.validationStatus}
                />

                <BieOnboardingStatusChip
                  kind="READINESS"
                  value={summary.validationReady}
                />
              </Stack>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 0.75,
                }}
              >
                {loadSession.code}
                {' · '}
                {getBieLoadSessionStateLabel(loadSession.stateCode)}
              </Typography>

              {loadSession.description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 1,
                    maxWidth: 900,
                  }}
                >
                  {loadSession.description}
                </Typography>
              )}
            </Box>

            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              sx={{
                flexShrink: 0,
              }}
            >
              {this.props.onRefresh && (
                <Tooltip title="Refresh Workbench" arrow>
                  <span>
                    <IconButton
                      disabled={this.isBusy()}
                      onClick={this.props.onRefresh}
                    >
                      <RefreshOutlinedIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              {this.renderPrimaryAction()}
            </Stack>
          </Stack>

          <Divider />

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              {this.renderMetadataItem(
                <SourceOutlinedIcon />,
                'Source Organization',
                loadSession.sourceOrganization
              )}
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              {this.renderMetadataItem(
                <DatasetOutlinedIcon />,
                'Load Scope',
                loadSession.scopeName || loadSession.scopeCode
              )}
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              {this.renderMetadataItem(
                <CalendarMonthOutlinedIcon />,
                'Historical Coverage',
                `${this.formatDate(loadSession.coverageStartDate)} through ${this.formatDate(
                  loadSession.coverageEndDate
                )}`
              )}
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              {this.renderMetadataItem(
                <AssessmentOutlinedIcon />,
                'Historical Cut-off',
                `${this.formatDate(loadSession.historicalCutoffDate)} · ${loadSession.sourceSystem || '—'}`
              )}
            </Grid>
          </Grid>
        </Stack>
      </Paper>
    );
  }

  private renderMetadataItem(
    icon: React.ReactElement,
    label: string,
    value: React.ReactNode
  ): React.ReactNode {
    return (
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Box
          sx={{
            width: 36,
            height: 36,

            borderRadius: 1.5,

            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',

            flexShrink: 0,

            color: 'primary.main',

            backgroundColor: 'action.hover',

            '& svg': {
              fontSize: 20,
            },
          }}
        >
          {icon}
        </Box>

        <Box
          sx={{
            minWidth: 0,
          }}
        >
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {label}
          </Typography>

          <Typography
            variant="body2"
            fontWeight={700}
            sx={{
              mt: 0.2,
              overflowWrap: 'anywhere',
            }}
          >
            {value || '—'}
          </Typography>
        </Box>
      </Stack>
    );
  }

  /* ==========================================================
   * Primary workflow action
   * ==========================================================
   */

  private renderPrimaryAction(): React.ReactNode {
    const workbench = this.requireWorkbench();

    const { actions, loadSession } = workbench;

    const {
      validating = false,
      publishing = false,
      onValidate,
      onPublish,
    } = this.props;

    if (actions.canValidate || actions.canRevalidate) {
      return (
        <Button
          variant="contained"
          color={actions.canRevalidate ? 'warning' : 'primary'}
          disabled={validating || publishing || !onValidate}
          startIcon={<FactCheckOutlinedIcon />}
          onClick={onValidate}
        >
          {validating
            ? 'Validating…'
            : actions.canRevalidate
              ? 'Revalidate Workbook'
              : 'Validate Workbook'}
        </Button>
      );
    }

    if (actions.canPublish) {
      return (
        <Button
          variant="contained"
          color="success"
          disabled={publishing || validating || !onPublish}
          startIcon={<PublishOutlinedIcon />}
          onClick={onPublish}
        >
          {publishing ? 'Publishing…' : 'Publish Validated Data'}
        </Button>
      );
    }

    return (
      <Button
        variant="outlined"
        color="inherit"
        disabled
        startIcon={this.getStateActionIcon(loadSession.stateCode)}
      >
        {getBieLoadSessionStateLabel(loadSession.stateCode)}
      </Button>
    );
  }

  /* ==========================================================
   * Summary cards
   * ==========================================================
   */

  private renderSummaryCards(): React.ReactNode {
    const summary = this.requireWorkbench().summary;

    return (
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
          <BieOnboardingSummaryCard
            title="Imported Records"
            subtitle="All active staged rows"
            value={summary.items.total}
            icon={DatasetOutlinedIcon}
            tone="PRIMARY"
            compact
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
          <BieOnboardingSummaryCard
            title="Valid"
            subtitle="Passed validation"
            value={summary.items.valid}
            icon={CheckCircleOutlineIcon}
            tone="SUCCESS"
            compact
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
          <BieOnboardingSummaryCard
            title="Warnings"
            subtitle="Non-blocking review"
            value={summary.items.warning}
            icon={WarningAmberOutlinedIcon}
            tone="WARNING"
            compact
            clickable={
              summary.issues.warning > 0 && Boolean(this.props.onViewAllIssues)
            }
            onClick={this.props.onViewAllIssues}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
          <BieOnboardingSummaryCard
            title="Failed"
            subtitle="Rows with blocking errors"
            value={summary.items.failed}
            icon={ErrorOutlineIcon}
            tone="ERROR"
            compact
            clickable={
              summary.items.failed > 0 && Boolean(this.props.onViewAllIssues)
            }
            onClick={this.props.onViewAllIssues}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
          <BieOnboardingSummaryCard
            title="Blocking Issues"
            subtitle="Must be corrected"
            value={summary.issues.blocking}
            icon={BlockOutlinedIcon}
            tone="ERROR"
            compact
            clickable={
              summary.issues.blocking > 0 && Boolean(this.props.onViewAllIssues)
            }
            onClick={this.props.onViewAllIssues}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
          <BieOnboardingSummaryCard
            title="Unresolved"
            subtitle="Reference resolution"
            value={summary.resolution.unresolvedReferences}
            icon={LinkOffOutlinedIcon}
            tone="WARNING"
            compact
          />
        </Grid>
      </Grid>
    );
  }

  /* ==========================================================
   * Validation breakdown
   * ==========================================================
   */

  private renderValidationBreakdown(): React.ReactNode {
    const summary = this.requireWorkbench().summary;

    const stateTotal = Math.max(Number(summary.items.total) || 0, 1);

    return (
      <Paper
        variant="outlined"
        sx={{
          height: '100%',
          p: 2.25,
        }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Validation Breakdown
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Distribution of staged records and validation findings.
            </Typography>
          </Box>

          <Divider />

          <Stack spacing={1.5}>
            {summary.byValidationState.map((item) => {
              const count = Number(item.count) || 0;

              const percentage = Math.round((count / stateTotal) * 100);

              return (
                <Box key={String(item.stateId)}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      mb: 0.65,
                    }}
                  >
                    <BieOnboardingStatusChip
                      kind="VALIDATION_STATE"
                      value={item.stateCode}
                    />

                    <Typography variant="body2" fontWeight={700}>
                      {this.formatNumber(count)}
                      {' · '}
                      {percentage}%
                    </Typography>
                  </Stack>

                  <LinearProgress
                    variant="determinate"
                    value={percentage}
                    color={this.getValidationStateColor(item.stateCode)}
                    sx={{
                      height: 7,
                      borderRadius: 4,
                    }}
                  />
                </Box>
              );
            })}
          </Stack>

          <Divider />

          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{
                mb: 1.25,
              }}
            >
              Issue Severity
            </Typography>

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {summary.bySeverity.length === 0 && (
                <Chip
                  size="small"
                  color="success"
                  variant="outlined"
                  icon={<CheckCircleOutlineIcon />}
                  label="No validation issues"
                />
              )}

              {summary.bySeverity.map((severity) => (
                <BieOnboardingStatusChip
                  key={String(severity.severityId)}
                  kind="SEVERITY"
                  value={severity.severityCode}
                  blocking={severity.isBlocking}
                  label={`${severity.severityName}: ${this.formatNumber(
                    severity.count
                  )}`}
                />
              ))}
            </Stack>
          </Box>

          <Divider />

          {this.renderResolutionBreakdown()}
        </Stack>
      </Paper>
    );
  }

  private renderResolutionBreakdown(): React.ReactNode {
    const breakdown = this.requireWorkbench().summary.resolution.breakdown;

    const entries: Array<{
      label: string;
      value: number;
    }> = [
      {
        label: 'Record Class',
        value: Number(breakdown.recordClass) || 0,
      },
      {
        label: 'Class Compatibility',
        value: Number(breakdown.classCompatibility) || 0,
      },
      {
        label: 'Metric / Driver',
        value: Number(breakdown.driver) || 0,
      },
      {
        label: 'Product',
        value: Number(breakdown.product) || 0,
      },
      {
        label: 'Location',
        value: Number(breakdown.location) || 0,
      },
      {
        label: 'Entity',
        value: Number(breakdown.entity) || 0,
      },
      {
        label: 'Period',
        value: Number(breakdown.period) || 0,
      },
      {
        label: 'Account',
        value: Number(breakdown.account) || 0,
      },
      {
        label: 'Unit',
        value: Number(breakdown.unit) || 0,
      },
    ];

    return (
      <Box>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{
            mb: 1.25,
          }}
        >
          Unresolved References
        </Typography>

        <Grid container spacing={1}>
          {entries.map((entry) => (
            <Grid size={{ xs: 6, sm: 4 }} key={entry.label}>
              <Box
                sx={{
                  p: 1,

                  borderRadius: 1.5,

                  border: '1px solid',

                  borderColor: entry.value > 0 ? 'warning.main' : 'divider',

                  backgroundColor:
                    entry.value > 0 ? 'warning.main' + '0A' : 'action.hover',
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  {entry.label}
                </Typography>

                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color={entry.value > 0 ? 'warning.main' : 'text.primary'}
                >
                  {this.formatNumber(entry.value)}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  /* ==========================================================
   * Workflow readiness
   * ==========================================================
   */

  private renderWorkflowReadiness(): React.ReactNode {
    const workbench = this.requireWorkbench();

    const steps = this.getWorkflowSteps();

    return (
      <Paper
        variant="outlined"
        sx={{
          height: '100%',
          p: 2.25,
        }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Workflow Readiness
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Current position in the onboarding lifecycle.
            </Typography>
          </Box>

          <Divider />

          <Stack spacing={1.25}>
            {steps.map((step, index) => (
              <Stack
                key={step.code}
                direction="row"
                spacing={1.25}
                alignItems="flex-start"
              >
                <Box
                  sx={{
                    width: 38,
                    height: 38,

                    borderRadius: '50%',

                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',

                    flexShrink: 0,

                    color: step.completed
                      ? 'success.contrastText'
                      : step.current
                        ? 'primary.contrastText'
                        : 'text.secondary',

                    backgroundColor: step.completed
                      ? 'success.main'
                      : step.current
                        ? 'primary.main'
                        : 'action.selected',

                    '& svg': {
                      fontSize: 20,
                    },
                  }}
                >
                  {step.icon}
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,

                    pb: index < steps.length - 1 ? 1.25 : 0,

                    borderBottom:
                      index < steps.length - 1 ? '1px solid' : 'none',

                    borderColor: 'divider',
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1}
                  >
                    <Typography variant="body2" fontWeight={700}>
                      {step.label}
                    </Typography>

                    <Chip
                      size="small"
                      variant="outlined"
                      color={
                        step.completed
                          ? 'success'
                          : step.current
                            ? 'primary'
                            : 'default'
                      }
                      label={
                        step.completed
                          ? 'Complete'
                          : step.current
                            ? 'Current'
                            : step.available
                              ? 'Available'
                              : 'Pending'
                      }
                    />
                  </Stack>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      mt: 0.35,
                    }}
                  >
                    {step.description}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Stack>

          <Divider />

          <Alert
            severity={
              workbench.actions.canPublish
                ? 'success'
                : workbench.summary.issues.blocking > 0
                  ? 'error'
                  : 'info'
            }
            icon={
              workbench.actions.canPublish ? (
                <VerifiedOutlinedIcon />
              ) : undefined
            }
          >
            {this.getReadinessMessage()}
          </Alert>
        </Stack>
      </Paper>
    );
  }

  private getWorkflowSteps(): IBieWorkflowStep[] {
    const workbench = this.requireWorkbench();

    const stateCode = String(workbench.loadSession.stateCode).toUpperCase();

    const order = ['DRAFT', 'STAGED', 'VALIDATED', 'PUBLISHED'];

    const normalizedState = stateCode === 'REJECTED' ? 'STAGED' : stateCode;

    const currentIndex = order.indexOf(normalizedState);

    return [
      {
        code: 'DRAFT',

        label: 'Workbook Preparation',

        description:
          'Upload the institution workbook and review the extracted sheets.',

        completed: currentIndex > 0,

        current: stateCode === 'DRAFT',

        available: workbench.actions.canUpload,

        icon: <UploadFileOutlinedIcon />,
      },

      {
        code: 'STAGED',

        label: 'Staging and Resolution',

        description:
          'Resolve controlled references and prepare records for validation.',

        completed: currentIndex > 1,

        current: stateCode === 'STAGED' || stateCode === 'REJECTED',

        available:
          workbench.actions.canStage ||
          workbench.actions.canValidate ||
          workbench.actions.canRevalidate,

        icon: <DatasetOutlinedIcon />,
      },

      {
        code: 'VALIDATED',

        label: 'Validation',

        description:
          'Apply business, reference, period, unit and reconciliation rules.',

        completed: stateCode === 'PUBLISHED',

        current: stateCode === 'VALIDATED',

        available:
          workbench.actions.canValidate || workbench.actions.canRevalidate,

        icon: <FactCheckOutlinedIcon />,
      },

      {
        code: 'PUBLISHED',

        label: 'Authoritative Publication',

        description:
          'Publish approved master data, historical facts, forecasts and assumptions.',

        completed: stateCode === 'PUBLISHED',

        current: false,

        available: workbench.actions.canPublish,

        icon: <PublishOutlinedIcon />,
      },
    ];
  }

  private getReadinessMessage(): string {
    const workbench = this.requireWorkbench();

    const { actions, summary, loadSession } = workbench;

    if (loadSession.stateCode === 'PUBLISHED') {
      return (
        'The Load Session has been published to the ' +
        'authoritative BIE data model.'
      );
    }

    if (actions.canPublish) {
      return (
        'All active records passed validation. The Load ' +
        'Session is ready for authoritative publication.'
      );
    }

    if (summary.issues.blocking > 0) {
      return (
        `${this.formatNumber(
          summary.issues.blocking
        )} blocking validation issue(s) must be corrected ` +
        'before the Load Session can be published.'
      );
    }

    if (summary.resolution.unresolvedReferences > 0) {
      return (
        `${this.formatNumber(
          summary.resolution.unresolvedReferences
        )} unresolved reference(s) remain. Re-run validation ` +
        'after correcting the staged data.'
      );
    }

    if (actions.canValidate || actions.canRevalidate) {
      return 'The staged workbook is ready for validation.';
    }

    return 'Complete the current workflow activity before proceeding.';
  }

  /* ==========================================================
   * Imports
   * ==========================================================
   */

  private renderImportSection(): React.ReactNode {
    const workbench = this.requireWorkbench();

    return (
      <Box>
        <Stack
          direction={{
            xs: 'column',
            sm: 'row',
          }}
          justifyContent="space-between"
          alignItems={{
            xs: 'stretch',
            sm: 'center',
          }}
          spacing={1}
          sx={{
            mb: 1.5,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Workbook Imports
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Validation status by workbook worksheet and import class.
            </Typography>
          </Box>

          <Chip
            variant="outlined"
            label={`${this.formatNumber(workbench.imports.length)} imports`}
          />
        </Stack>

        {workbench.imports.length === 0 ? (
          <Paper
            variant="outlined"
            sx={{
              py: 6,
              px: 3,
              textAlign: 'center',
            }}
          >
            <UploadFileOutlinedIcon
              color="disabled"
              sx={{
                fontSize: 48,
              }}
            />

            <Typography
              variant="h6"
              fontWeight={700}
              sx={{
                mt: 1,
              }}
            >
              No workbook imports
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,
              }}
            >
              Upload an institution onboarding workbook to begin staging and
              validation.
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {workbench.imports.map((item) => (
              <Grid size={{ xs: 12, md: 6, xl: 4 }} key={String(item.rid)}>
                <BieOnboardingImportCard
                  data={item}
                  disabled={this.isBusy()}
                  clickable={false}
                  onViewIssues={this.props.onViewImportIssues}
                  onViewRecords={this.props.onViewImportRecords}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    );
  }

  /* ==========================================================
   * Recent issues
   * ==========================================================
   */

  private renderRecentIssues(): React.ReactNode {
    const workbench = this.requireWorkbench();

    const issues = workbench.recentIssues.slice(0, 8);

    return (
      <Paper
        variant="outlined"
        sx={{
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            p: 2,
          }}
        >
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
            justifyContent="space-between"
            alignItems={{
              xs: 'stretch',
              sm: 'center',
            }}
            spacing={1}
          >
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Recent Validation Issues
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Highest-impact findings from the latest validation run.
              </Typography>
            </Box>

            {this.props.onViewAllIssues && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<RuleOutlinedIcon />}
                onClick={this.props.onViewAllIssues}
              >
                View All Issues
              </Button>
            )}
          </Stack>
        </Box>

        <Divider />

        {issues.length === 0 ? (
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
            }}
          >
            <CheckCircleOutlineIcon
              color="success"
              sx={{
                fontSize: 42,
              }}
            />

            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{
                mt: 1,
              }}
            >
              No active validation issues
            </Typography>

            <Typography variant="body2" color="text.secondary">
              The current onboarding records have no active validation findings.
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {issues.map((issue, index) => (
              <React.Fragment key={String(issue.rid)}>
                <ListItem
                  disablePadding
                  secondaryAction={
                    this.props.onOpenIssueRecord ? (
                      <Tooltip title="Open staged record" arrow>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => this.handleOpenIssueRecord(issue)}
                        >
                          <OpenInNewOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : undefined
                  }
                >
                  <ListItemButton
                    disabled={!this.props.onOpenIssueRecord}
                    onClick={() => this.handleOpenIssueRecord(issue)}
                  >
                    <Stack
                      direction={{
                        xs: 'column',
                        sm: 'row',
                      }}
                      spacing={1.5}
                      alignItems={{
                        xs: 'flex-start',
                        sm: 'center',
                      }}
                      sx={{
                        width: '100%',
                        pr: 3,
                      }}
                    >
                      <BieOnboardingStatusChip
                        kind="SEVERITY"
                        value={issue.svc}
                        blocking={issue.ibl}
                      />

                      <ListItemText
                        primary={issue.msg || issue.rln || issue.rlc}
                        secondary={
                          `${issue.ssh || 'Unknown Sheet'} · ` +
                          `Row ${this.formatNumber(
                            issue.srw
                          )} · ${getBieImportClassLabel(issue.clc)} · ${
                            issue.rlc
                          }`
                        }
                        primaryTypographyProps={{
                          variant: 'body2',
                          fontWeight: 600,
                        }}
                        secondaryTypographyProps={{
                          variant: 'caption',
                        }}
                      />
                    </Stack>
                  </ListItemButton>
                </ListItem>

                {index < issues.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>
    );
  }

  private handleOpenIssueRecord(issue: IBieOnboardingValidationIssue): void {
    if (!this.props.onOpenIssueRecord) {
      return;
    }

    this.props.onOpenIssueRecord(issue.iti, issue);
  }

  /* ==========================================================
   * Loading / unavailable states
   * ==========================================================
   */

  private renderLoading(): React.ReactNode {
    return (
      <Stack spacing={2.5}>
        <Paper
          variant="outlined"
          sx={{
            p: 2.5,
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between">
              <Box
                sx={{
                  width: '65%',
                }}
              >
                <Skeleton variant="text" width="70%" height={40} />

                <Skeleton variant="text" width="45%" />

                <Skeleton variant="text" width="85%" />
              </Box>

              <Skeleton variant="rounded" width={150} height={38} />
            </Stack>

            <Divider />

            <Grid container spacing={2}>
              {Array.from({
                length: 4,
              }).map((_, index) => (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                  <Skeleton variant="rounded" height={54} />
                </Grid>
              ))}
            </Grid>
          </Stack>
        </Paper>

        <Grid container spacing={2}>
          {Array.from({
            length: 6,
          }).map((_, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }} key={index}>
              <BieOnboardingSummaryCard title="" value="" loading compact />
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 7 }}>
            <Skeleton variant="rounded" height={430} />
          </Grid>

          <Grid size={{ xs: 12, lg: 5 }}>
            <Skeleton variant="rounded" height={430} />
          </Grid>
        </Grid>
      </Stack>
    );
  }

  private renderUnavailable(): React.ReactNode {
    return (
      <Paper
        variant="outlined"
        sx={{
          py: 8,
          px: 3,
          textAlign: 'center',
        }}
      >
        <AssessmentOutlinedIcon
          color="disabled"
          sx={{
            fontSize: 56,
          }}
        />

        <Typography
          variant="h6"
          fontWeight={700}
          sx={{
            mt: 1,
          }}
        >
          Workbench data unavailable
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mt: 0.75,
          }}
        >
          The selected Load Session could not be loaded.
        </Typography>

        {this.props.onRefresh && (
          <Button
            variant="outlined"
            startIcon={<RefreshOutlinedIcon />}
            sx={{
              mt: 2,
            }}
            onClick={this.props.onRefresh}
          >
            Retry
          </Button>
        )}
      </Paper>
    );
  }

  /* ==========================================================
   * Helpers
   * ==========================================================
   */

  private requireWorkbench(): IBieOnboardingWorkbench {
    if (!this.props.workbench) {
      throw new Error('BIE Onboarding Workbench data is required.');
    }

    return this.props.workbench;
  }

  private isBusy(): boolean {
    return Boolean(
      this.props.loading ||
        this.props.refreshing ||
        this.props.validating ||
        this.props.publishing
    );
  }

  private getValidationStateColor(
    stateCode: string
  ): 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
    switch (String(stateCode || '').toUpperCase()) {
      case 'VALID':
        return 'success';

      case 'WARNING':
        return 'warning';

      case 'FAILED':
        return 'error';

      case 'PENDING':
      default:
        return 'info';
    }
  }

  private getStateActionIcon(stateCode: string): React.ReactElement {
    switch (String(stateCode || '').toUpperCase()) {
      case 'VALIDATED':
        return <VerifiedOutlinedIcon />;

      case 'PUBLISHED':
        return <PublishOutlinedIcon />;

      case 'REJECTED':
        return <ErrorOutlineIcon />;

      case 'STAGED':
        return <FactCheckOutlinedIcon />;

      default:
        return <UploadFileOutlinedIcon />;
    }
  }

  private formatNumber(value: unknown): string {
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
      return '0';
    }

    return new Intl.NumberFormat().format(numericValue);
  }

  private formatDate(value: string | null | undefined): string {
    if (!value) {
      return 'Not specified';
    }

    const normalized = String(value).trim();

    const dateOnlyMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})/);

    if (!dateOnlyMatch) {
      return normalized;
    }

    const year = Number(dateOnlyMatch[1]);

    const month = Number(dateOnlyMatch[2]);

    const day = Number(dateOnlyMatch[3]);

    const date = new Date(year, month - 1, day);

    if (Number.isNaN(date.getTime())) {
      return normalized;
    }

    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    }).format(date);
  }
}
